import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { NaatiVisaLanding } from "@/content/naati-corpus/visa-subclasses";
import { NAATI_DOC_META, NAATI_VISA_META } from "@/content/naati-corpus/meta.gen";

function visaFaqsEn(v: NaatiVisaLanding) {
  return [
    { q: `Which documents need NAATI translation for the ${v.nameEn}?`, a: v.docsEn.map((d) => d.doc).join(" · ") },
    { q: `Who lodges the ${v.nameEn}?`, a: v.whoEn },
    { q: `Why do case officers request more documents for the ${v.nameEn}?`, a: v.pitfallsEn.join(" · ") },
    { q: `How long does the translation take?`, a: `${v.turnaroundEn} Pricing is per page or per word, and you get a free written quote before any work starts.` },
    {
      q: "Can I use a non-NAATI translator?",
      a: "Department of Home Affairs generally requires translations produced inside Australia to come from a NAATI-credentialled translator, while translations produced outside Australia must show the translator's full name, address, qualifications and experience. A NAATI Certified translation is the safest and most familiar option for case officers.",
    },
    {
      q: "Does a NAATI translation also need Thai MFA legalisation?",
      a: "For an Australian visa lodgement, generally no — NAATI is Australia's own translation credential. If the same documents are also used by Thai authorities or a third country, separate Department of Consular Affairs legalisation may be required. NAATI certification and Thai MFA translation certification are not interchangeable.",
    },
    {
      q: "Can I send scans instead of originals?",
      a: "Yes. High-resolution colour scans in which every stamp and signature is legible are sufficient for NAATI work. Originals are only needed when Thai MFA legalisation runs alongside the translation.",
    },
    {
      q: "Can you guarantee the visa outcome?",
      a: "No. Visa decisions rest solely with the Department of Home Affairs. We are responsible for the accuracy and completeness of the translation, and we correct translator errors free of charge.",
    },
  ];
}

export const Route = createFileRoute("/en/naati/visa/$subclass")({
  loader: async ({ params }): Promise<{ v: NaatiVisaLanding }> => {
    const { getNaatiVisaLanding } = await import("@/content/naati-corpus/visa-subclasses");
    const v = getNaatiVisaLanding(params.subclass);
    if (!v) throw notFound();
    return { v };
  },
  component: NaatiVisaPageEN,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Visa subclass not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { v } = loaderData;
    const title = `${v.titleEn} (2026)`;
    const description = `Documents requiring NAATI translation for the ${v.nameEn} — checklist, the requests case officers make most often, and turnaround.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/naati/visa/${params.subclass}`,
        type: "article",
        alternates: [{ lang: "th", path: `/naati/visa/${params.subclass}` }],
        keywords: [
          v.titleEn,
          `naati translation subclass ${v.code}`,
          `subclass ${v.code} document checklist`,
          `${v.nameEn} translation requirements`,
          "naati certified translator thailand",
          "thai to english naati translation",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "NAATI Translation", url: `${site}/en/naati` },
              { name: v.nameEn, url: `${site}/en/naati/visa/${params.subclass}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(visaFaqsEn(v))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/naati/visa/${params.subclass}`)) },
      ],
    };
  },
});

function NaatiVisaPageEN() {
  const { v } = Route.useLoaderData();
  const faqs = visaFaqsEn(v);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/naati" className="hover:underline">NAATI Translation</a> <span>/</span> <span>{v.nameEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{v.titleEn}</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          The {v.nameEn} requires Thai-language documents to be lodged with verifiable English translations. The core set covers {v.docsEn.length} document
          groups, and {v.turnaroundEn} The two costliest mistakes are name spellings that differ from the passport and pages left untranslated.
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Get a free quote →</a>
          <a href="/en/naati/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">NAATI FAQ</a>
          <a href={`/naati/visa/${v.slug}`} className="rounded-md border px-4 py-2 font-medium hover:bg-muted">ภาษาไทย</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Who lodges this visa</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{v.whoEn}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">Documents needing NAATI translation for subclass {v.code}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pe-3 font-semibold">Document</th>
                  <th className="py-2 font-semibold">Check before translation</th>
                </tr>
              </thead>
              <tbody>
                {v.docsEn.map((d: { doc: string; note: string }, i: number) => (
                  <tr key={i} className="border-b align-top last:border-0">
                    <td className="py-2 pe-3 font-medium">{d.doc}</td>
                    <td className="py-2 text-muted-foreground">{d.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground"><strong>Turnaround:</strong> {v.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Why documents get queried or returned</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {v.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — {v.nameEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other visa subclasses</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_VISA_META.filter((x) => x.slug !== v.slug).map((x) => (
              <a key={x.slug} href={`/en/naati/visa/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                subclass {x.code}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">NAATI translation by document type</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_DOC_META.map((x) => (
              <a key={x.slug} href={`/en/naati/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn.replace("NAATI Translation of ", "")}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Official source:{" "}
          <a href={v.officialUrl} rel="nofollow noopener" target="_blank" className="text-primary underline">
            {v.officialLabel}
          </a>{" "}
          — requirements and processing times change; verify with the authority before acting. Visa decisions rest solely with the immigration authority.
        </p>

        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
